import React, { useEffect, useMemo } from "react";
import Text from "@ui/Text";
import ButtonIcon from "@ui/ButtonIconV2";
import { CloseLine } from "@ui/IconV2/react";
import { colors, fonts, spacings } from "@ui/Theme";
import t from "../../../../../../../../../i18n";
import tui from "@ui/i18n";
import { type FaultEvent } from "../../../../../../../../../api/useFaultEvents";
import SidePanel from "../../../../components/SidePanel";
import { css } from "@linaria/core";
import InputV2 from "@ui/Input/InputV2";
import useInput from "@ui/lib/hooks/useInput";
import { isLeft } from "fp-ts/lib/Either";
import { short, veryLong } from "diagnostics-lib/io";
import * as io from "io-ts";
import Select from "@ui/Select";
import { generateFaultEventPayload } from "diagnostics-domain/faultEvent";
import type { FaultEventStatus, PriorityFaultEvent } from "diagnostics-domain/faultEvent";
import { getStatusOptions } from "../../../../../../utils/faultEventStatus";
import Button from "@ui/Button";
import RadioButton from "@ui/RadioButton";
import { getFaultEventDetectionText } from "../../../../../../utils/faultEventDetection";
import { NONE_LABEL_FAILURE_STATUS } from "../../../../../../../../../api/useFailureMetadata";
import { buildOptions, NONE_LABEL } from "@ui/Filter";
import { reparabilities, type Reparability } from "../../../../../../../../../api/Reparability";
import { useTranslations } from "../../../../../../../../../api/useTranslations";
import { Markdown } from "@ui/Markdown";
import classNames from "classnames";
import AdditionnalMessage from "@ui/AdditionnalMessage";
import { getFaultStatusText } from "../../../../../../../Fault";
import { usePost, usePut } from "../../../../../../../../../api/useCall";
import useToasts from "@ui/Toast/useToasts";
import OverlayWrapper from "@ui/TooltipV2/OverlayWrapper";
import { useSettings } from "../../../../../../../../../api/useSettings";
import { getFaultEventPriority } from "../../../../../../utils/faultEvent";
import type { FaultEventSidePanelFocusField } from "../../../../../../hooks/createGlobalStates/useSelectedSidePanelFaultEvent";
import { EVENT_PRIORITIES, getEventPriorityText } from "../../../../../../utils/event";
import type { LexicalEditor } from "lexical";

interface Props {
  projectId: string;
  faultEvent: FaultEvent;
  isFolded: boolean;
  closePanel: () => void;
  focusField?: FaultEventSidePanelFocusField;
  initialStatus?: FaultEventStatus;
}

const REPARABILITY_WEIGHT: Record<Reparability, number> = {
  REPARABILITY_0: 0,
  REPARABILITY_1: 1,
  REPARABILITY_2: 2,
};

const Parameters = ({
  isFolded,
  projectId,
  faultEvent,
  closePanel,
  focusField,
  initialStatus,
}: Props) => {
  const { addToast } = useToasts();
  const translations = useTranslations();
  const createComment = usePost("/comments");
  const updateFaultEvent = usePut("/projects/:projectId(uuid)/faultEvents");

  // Pre-compute
  const statusesByAnalysis = React.useMemo(
    () => faultEvent.metadata.faults.flatMap(fault => fault.statusByAnalysis),
    [faultEvent.metadata.faults],
  );

  const faultsConfirmed = React.useMemo(
    () => statusesByAnalysis.filter(status => status.status === "CONFIRMED"),
    [statusesByAnalysis],
  );
  const faultsDetectionsFalse = React.useMemo(
    () =>
      statusesByAnalysis.filter(
        status => status.status === "REJECTED" || status.status === "LEGACY_REJECTED",
      ),
    [statusesByAnalysis],
  );
  const areAllFaultsConfirmed = faultsConfirmed.length === statusesByAnalysis.length;
  const areAllFalseDetection =
    statusesByAnalysis.length > 0 && faultsDetectionsFalse.length === statusesByAnalysis.length;
  // Variables
  const isAutoConfirmedStatus = faultEvent.metadata.newDiagnosesStatus === "CONFIRMED";
  const isAutoRejectedStatus =
    faultEvent.metadata.newDiagnosesStatus === "LEGACY_REJECTED" ||
    faultEvent.metadata.newDiagnosesStatus === "REJECTED";

  // Name
  const {
    defaultValue: defaultFaultEventNameInput,
    value: faultEventNameInput,
    dirty: dirtyFaultEventNameInput,
    onChange: handlerFaultEventNameInput,
  } = useInput(faultEvent.name);
  const errorFaultEventNameInput: string | undefined = React.useMemo(() => {
    const trimFaultEventNameInput = faultEventNameInput.trim();
    if (!trimFaultEventNameInput)
      return t("project.fault_event.fault.hearder.editing.name.error.cannot_be_empty");
    if (isLeft(short(io.string).decode(trimFaultEventNameInput))) {
      return t("project.fault_event.fault.hearder.editing.name.error.cannot_exceed_100_characters");
    }
    return;
  }, [faultEventNameInput]);

  // Priority
  const disableMediumPriority = useSettings().FAULT_EVENTS_DISABLE_MEDIUM_PRIORITY;
  const originalDefaultPriority = useMemo(() => getFaultEventPriority(faultEvent), [faultEvent]);
  const defaultPriority =
    disableMediumPriority && originalDefaultPriority === "MEDIUM"
      ? "HIGH"
      : originalDefaultPriority;
  const priorityOptions = React.useMemo(
    () =>
      EVENT_PRIORITIES.filter(priority => !disableMediumPriority || priority !== "MEDIUM").map(
        priority => ({
          label: getEventPriorityText(priority),
          value: priority,
        }),
      ),
    [disableMediumPriority],
  );
  const [priority, setPriority] = React.useState(defaultPriority);

  /** @note Force priority regarding to application settings for current event */
  useEffect(() => {
    if (disableMediumPriority && priority === "MEDIUM") {
      setPriority("HIGH");
    }
  }, [disableMediumPriority, priority, originalDefaultPriority]);

  const dirtyPriority = defaultPriority !== priority;

  // Status
  const defaultStatus = faultEvent.metadata.status;
  const statusOptions = React.useMemo(() => getStatusOptions(defaultStatus), [defaultStatus]);
  const [status, setStatus] = React.useState(initialStatus ?? defaultStatus);
  const dirtyStatus = status !== defaultStatus;

  useEffect(() => {
    if (initialStatus !== undefined) {
      setStatus(initialStatus);
    }
  }, [initialStatus]);

  useEffect(() => {
    if (
      focusField === "actionIdentified" &&
      status === "ACTION_IDENTIFIED" &&
      actionEditorRef.current
    ) {
      actionEditorRef.current.focus();
    }
  }, [focusField, status]);

  useEffect(() => {
    if (focusField === "status" && statusEditorRef.current) {
      statusEditorRef.current.focus();
    }
  }, [focusField, status]);

  // Detection type
  const defaultDetectionType = React.useMemo(() => {
    if (faultEvent.detection !== null) return faultEvent.detection;

    return faultsDetectionsFalse.length <= faultsConfirmed.length;
  }, [faultEvent, faultsConfirmed, faultsDetectionsFalse]);
  const canEditDetectionType = React.useMemo(() => status === "CLOSED", [status]);
  const [detectionType, setDetectionType] = React.useState(defaultDetectionType);
  const hasConflictingStatusBetweenDetection =
    (detectionType && (!areAllFaultsConfirmed || !isAutoConfirmedStatus)) ||
    (!detectionType && (!areAllFalseDetection || !isAutoRejectedStatus));
  const dirtyDetectionType = defaultDetectionType !== detectionType;

  // Repair category
  const canEditRepairCategory = React.useMemo(
    () => status === "ACTION_IDENTIFIED" || (status === "CLOSED" && detectionType),
    [status, detectionType],
  );
  const highestFaultReparability = React.useMemo(
    () =>
      faultEvent.metadata.faults.reduce<Reparability | null>((highest, fault) => {
        const current = fault.reparability;
        if (current === null) return highest;
        if (highest === null || REPARABILITY_WEIGHT[current] > REPARABILITY_WEIGHT[highest]) {
          return current;
        }
        return highest;
      }, null),
    [faultEvent.metadata.faults],
  );
  const defaultRepairCategory = React.useMemo(() => {
    if (status === NONE_LABEL_FAILURE_STATUS || status === "ONGOING") {
      return NONE_LABEL;
    }
    if (defaultStatus === "ONGOING") {
      return highestFaultReparability ?? NONE_LABEL;
    }
    return faultEvent.reparability ?? NONE_LABEL;
  }, [faultEvent.reparability, status, defaultStatus, highestFaultReparability]);
  const {
    dirty: dirtyRepairCategory,
    value: repairCategory,
    onChange: onChangeRepairCategory,
    setValue: setRepairCategory,
  } = useInput(defaultRepairCategory);
  const repairCategoryOptions = React.useMemo(
    () =>
      [{ label: NONE_LABEL, value: NONE_LABEL }].concat(
        buildOptions(
          reparabilities.map(v => ({
            label: t("reparability", v, translations),
            value: v,
          })),
        ),
      ),
    [translations],
  );

  // Action
  const defaultAction = faultEvent.action ?? "";
  const [action, setAction] = React.useState(defaultAction);
  const errorAction = React.useMemo(() => {
    if (defaultAction !== action && isLeft(veryLong(io.string).decode(action))) {
      return t("project.fault_event.modal.form.change_status.input.action_identified.error");
    }
    return;
  }, [defaultAction, action]);
  const dirtyAction = defaultAction !== action;

  // Comment
  const [comment, setComment] = React.useState<string>("");
  const dirtyComment = comment.trim() !== "";

  const faultEventNameInputRef = React.useRef<HTMLInputElement | null>(null);
  const actionEditorRef = React.useRef<LexicalEditor | null>(null);
  const statusEditorRef = React.useRef<HTMLSelectElement | null>(null);

  const handleSubmit = React.useCallback(async () => {
    const newStatus = dirtyStatus ? status : faultEvent.metadata.status;
    const newName = dirtyFaultEventNameInput ? faultEventNameInput.trim() : faultEvent.name;
    const newPriority = dirtyPriority ? priority : faultEvent.priority;
    const newAction =
      dirtyAction && newStatus === "ACTION_IDENTIFIED" ? action.trim() : faultEvent.action;
    const newDetection = newStatus === "CLOSED" ? detectionType : null;
    const newRepairCategory =
      status === "CLOSED" && detectionType === false
        ? null
        : repairCategory === NONE_LABEL
          ? null
          : (repairCategory as Reparability);
    const payloadFaultEvent = generateFaultEventPayload({
      faultEvent: {
        ...faultEvent,
        name: newName,
        priority: newPriority,
        closedAt: newStatus === "CLOSED" ? new Date() : null,
        identifiedAction: newStatus === "ACTION_IDENTIFIED",
        action: newAction,
        detection: newDetection,
        reparability: newRepairCategory,
      },
    });
    try {
      await updateFaultEvent({
        body: payloadFaultEvent,
        routeParams: { projectId },
      });
      const newComment = comment.trim();
      if (newComment !== "") {
        await createComment({
          body: {
            type: "RUN",
            projectId,
            analysisId: null,
            parentId: null,
            content: newComment,
            dashboardId: null,
            blockCommentId: null,
            manualFaultEventId: null,
            faultEventId: faultEvent.id,
          },
        });
      }
      addToast(
        "success",
        t("project.fault_event.evolution_chart.change_fault_event_status.modal.form.toast.success"),
      );
      closePanel();
    } catch {
      addToast("error", t("errors.backend_error"));
    }
    return true;
  }, [
    addToast,
    closePanel,
    projectId,
    updateFaultEvent,
    createComment,
    comment,
    faultEvent,
    dirtyFaultEventNameInput,
    faultEventNameInput,
    dirtyPriority,
    priority,
    dirtyStatus,
    status,
    detectionType,
    repairCategory,
    dirtyAction,
    action,
  ]);

  const error = errorFaultEventNameInput !== undefined || errorAction !== undefined;

  const dirty =
    (dirtyFaultEventNameInput ||
      dirtyPriority ||
      dirtyStatus ||
      dirtyDetectionType ||
      dirtyRepairCategory ||
      dirtyAction ||
      dirtyComment) &&
    !error;

  return (
    <SidePanel
      isFolded={isFolded}
      header={
        <>
          <Text type="h4">{t("project.fault_event.side_panel.fault_event_details.title")}</Text>
          <ButtonIcon onClick={closePanel}>
            <CloseLine size="m" color={colors.carbon800} />
          </ButtonIcon>
        </>
      }
    >
      <div className={parameters}>
        <div className={parameters_input}>
          <InputV2
            className={featureInput}
            label={t("project.fault_event.side_panel.fault_event_details.input.name")}
            placeholder=""
            value={faultEventNameInput}
            onChange={handlerFaultEventNameInput}
            error={errorFaultEventNameInput}
            defaultValue={defaultFaultEventNameInput}
            revertable={dirtyFaultEventNameInput}
            required
            ref={faultEventNameInputRef}
          />

          <div>
            <Select
              className={featureInput}
              label={t("project.fault_event.side_panel.fault_event_details.select.priority")}
              prompt=""
              defaultValue={defaultPriority}
              value={priority}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                setPriority(e.target.value as PriorityFaultEvent)
              }
              options={priorityOptions}
              revertable={priority !== defaultPriority}
              revert={() => setPriority(defaultPriority)}
              clearable={false}
              tooltipOverlay={
                <OverlayWrapper
                  text={t(
                    "project.fault_event.side_panel.fault_event_details.select.priority.tooltip",
                  )}
                />
              }
              disabled={faultEvent.metadata.status === "CLOSED"}
            />
            {faultEvent.metadata.priority !== priority && (
              <AdditionnalMessage
                className={priorityInfo}
                status="INFO"
                text={t(
                  "project.fault_event.side_panel.fault_event_details.select.priority.hint",
                  getEventPriorityText(faultEvent.metadata.priority),
                )}
              />
            )}
          </div>

          <Select
            ref={statusEditorRef}
            className={featureInput}
            label={t("project.fault_event.modal_form.select.status.label")}
            prompt=""
            defaultValue={defaultStatus}
            value={status}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setStatus(e.target.value as FaultEventStatus)
            }
            options={statusOptions}
            revertable={dirtyStatus}
            revert={() => setStatus(defaultStatus)}
            clearable={false}
          />
          {canEditDetectionType && (
            <div className={featureDetectionType}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal_form.select.detection_type.label")}
              </Text>
              <div className={radioButtons}>
                <RadioButton checked={detectionType} onChange={() => setDetectionType(s => !s)}>
                  {getFaultEventDetectionText(true)}
                </RadioButton>
                <RadioButton checked={!detectionType} onChange={() => setDetectionType(s => !s)}>
                  {getFaultEventDetectionText(false)}
                </RadioButton>
              </div>
            </div>
          )}

          {canEditRepairCategory && (
            <Select
              className={featureInput}
              label={t("project.fault_event.modal_form.select.outage_type_for_repair.label")}
              prompt=""
              value={repairCategory}
              onChange={onChangeRepairCategory}
              options={repairCategoryOptions}
              revertable={dirtyRepairCategory}
              revert={() => setRepairCategory(defaultRepairCategory)}
              disabled={faultEvent.metadata.status === "CLOSED"}
              clearable={false}
            />
          )}

          {(status === "ACTION_IDENTIFIED" || status === "CLOSED") && (
            <div className={markdownWrapper}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal.form.change_status.input.action_identified.label")}
              </Text>
              {status === "CLOSED" ? (
                <div className={disabledActionMarkdown}>{action}</div>
              ) : (
                <Markdown
                  ref={actionEditorRef}
                  editable
                  autoFocus={focusField === "actionIdentified"}
                  defaultValue={action}
                  onConversion={m => setAction(m.trim())}
                  placeholder={t(
                    "project.fault_event.modal.form.change_status.input.action_identified.placeholder",
                  )}
                  className={classNames(markdownEditorError, { error: errorAction })}
                />
              )}
              {errorAction && (
                <AdditionnalMessage className={markdownError} status="ERROR" text={errorAction} />
              )}
            </div>
          )}

          {(dirtyStatus ||
            dirtyPriority ||
            dirtyRepairCategory ||
            dirtyAction ||
            dirtyDetectionType) && (
            <div className={markdownWrapper}>
              <Text type="body1SemiBold">
                {t("project.fault_event.modal.form.change_status.comment.label")}
              </Text>
              <Markdown
                editable
                autoFocus={false}
                onConversion={m => setComment(m.trim())}
                defaultValue={comment}
                placeholder={t("project.fault_event.modal.form.change_status.comment.placeholder")}
              />
              <Text type="body2">
                {t("project.fault_event.modal.form.change_status.comment.sub_comment")}
              </Text>
            </div>
          )}

          {dirtyStatus && status === "CLOSED" && detectionType ? (
            !areAllFaultsConfirmed && !isAutoConfirmedStatus ? (
              <AdditionnalMessage
                status="INFO"
                text={t(
                  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed_and_not_be_automatically_confirmed",
                  {
                    statusToNewDetection: getFaultStatusText(
                      faultEvent.metadata.newDiagnosesStatus,
                    ),
                  },
                )}
              />
            ) : areAllFaultsConfirmed ? (
              isAutoConfirmedStatus ? null : (
                <AdditionnalMessage
                  status="INFO"
                  text={t(
                    "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_be_automatically_confirmed",
                    {
                      statusToNewDetection: getFaultStatusText(
                        faultEvent.metadata.newDiagnosesStatus,
                      ),
                    },
                  )}
                />
              )
            ) : (
              <AdditionnalMessage
                status="INFO"
                text={t(
                  "project.fault_event.modal_form.change_fault_event_status.selected.detection.true.not_all_faults_confirmed",
                )}
              />
            )
          ) : null}

          {dirtyStatus && status === "CLOSED" && !detectionType ? (
            !areAllFalseDetection && !isAutoRejectedStatus ? (
              <AdditionnalMessage
                status="INFO"
                text={t(
                  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection_and_not_be_automatically_false_detection",
                  {
                    statusToNewDetection: getFaultStatusText(
                      faultEvent.metadata.newDiagnosesStatus,
                    ),
                  },
                )}
              />
            ) : areAllFalseDetection ? (
              isAutoRejectedStatus ? null : (
                <AdditionnalMessage
                  status="INFO"
                  text={t(
                    "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_be_automatically_false_detection",
                    {
                      statusToNewDetection: getFaultStatusText(
                        faultEvent.metadata.newDiagnosesStatus,
                      ),
                    },
                  )}
                />
              )
            ) : (
              <AdditionnalMessage
                status="INFO"
                text={t(
                  "project.fault_event.modal_form.change_fault_event_status.selected.detection.false.not_all_faults_false_detection",
                )}
              />
            )
          ) : null}

          {dirtyStatus && status === "CLOSED" && hasConflictingStatusBetweenDetection && (
            <Text>
              {t(
                "project.fault_event.modal_form.change_fault_event_status.selected.detection.true_or_false",
              )}
            </Text>
          )}
        </div>
      </div>
      <div className={parameters_submit}>
        <div className={parameters_submit__mandatory}>
          <sup className={parameters_submit__sup}>*</sup> {tui("input.mandatory")}
        </div>
        <div className={parameters_submit__action}>
          <Button onClick={closePanel}> {t("commons.message_card.cancel")}</Button>
          <Button variant="primary" disabled={!dirty} onClick={handleSubmit}>
            {t("commons.button.save")}
          </Button>
        </div>
      </div>
    </SidePanel>
  );
};

export default Parameters;

//#region CSS
const parameters = css`
  flex: 1;
  padding: ${spacings.m};
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const parameters_input = css`
  width: 100%;
  & > * + * {
    margin-top: ${spacings.m};
  }
`;
const featureInput = css`
  width: 100%;
`;
const featureDetectionType = css`
  & > * + * {
    margin-top: ${spacings.xs};
  }

  color: ${colors.carbon800};
`;
const radioButtons = css`
  padding-left: ${spacings.xxs};
  display: flex;
  gap: ${spacings.m};
`;
const markdownWrapper = css`
  & > * + * {
    margin-top: ${spacings.xs};
  }
  color: ${colors.carbon800};
`;
const markdownEditorError = css`
  &.editable {
    &.error {
      border-color: ${colors.oxygen400};
    }
  }
`;
const markdownError = css`
  margin-top: ${spacings.xxs};
`;
const disabledActionMarkdown = css`
  min-height: 34px;
  padding: ${spacings.xs} ${spacings.s};
  border: 1px solid ${colors.titanium200};
  border-radius: 4px;
  background-color: ${colors.titanium50};
  color: ${colors.carbon900};
  font: ${fonts.body1};
  opacity: 0.5;
  white-space: pre-wrap;
  overflow-wrap: break-word;
`;

const parameters_submit = css`
  position: sticky;
  bottom: 0;
  padding: ${spacings.m};
  display: flex;
  justify-content: space-between;
  border-top: 1px solid ${colors.titanium200};
  background-color: ${colors.titanium50};
`;

const parameters_submit__sup = css`
  color: ${colors.darkNitrogen600};
  vertical-align: bottom;
`;
const parameters_submit__mandatory = css`
  align-self: flex-end;
  font: ${fonts.body3};
  color: ${colors.darkNitrogen600};
`;
const parameters_submit__action = css`
  * + * {
    margin-left: ${spacings.xs};
  }
`;

const priorityInfo = css`
  margin-top: ${spacings.xs};
`;
//#endregion CSS
